// Host <-> sandbox protocol (v1). After a handshake on window.postMessage, everything goes over one
// MessageChannel port. Every message from the sandbox is untrusted: the host validates shapes and never
// renders strings as HTML.
import type { Manifest } from './manifest.ts';
import { channelMax } from './net.ts';

export const PROTOCOL_VERSION = 1;

/** Sandbox -> parent window, once the loader runs: "I am here, send me a port". */
export interface HelloMsg { rtw: 'hello'; v: number }
/** Parent -> sandbox window, with the port transferred. */
export interface ConnectMsg { rtw: 'connect'; v: number }

/** rx/ry (v1.1): the right analog stick, -127..127, up positive; sent to rtw_rstick when the module exports it. */
export interface PadState { buttons: number; sx: number; sy: number; rx?: number; ry?: number }

export interface LoadPayload {
  manifest: {
    id: string; platform: string; loop: string; video: string; hz: number; factory?: string;
    memory?: { initialMB?: number; maximumMB?: number; memory64?: boolean };
  };
  buildId: string; // 64 hex
  wasm: ArrayBuffer;
  js?: ArrayBuffer;
  data: Record<string, ArrayBuffer>;
  rom: ArrayBuffer;
  save?: ArrayBuffer;
  options: [number, number][];
  /** v1.2 lockstep: this run is a session boot (rtw_net_session is called before rtw_start). */
  net?: NetStart;
}

/** v1.2: a session as the module sees it. Peers are session indexes (0 = room host). */
export interface NetStart {
  players: number;
  me: number;
  model: 'lockstep' | 'link';
  /** rtw_net_peer state per index (the module's own index: 1). */
  peers: number[];
  /** [a, b, median ms, p90 ms] per pair of indexes, measured in the room before the start. */
  pairs: [number, number, number, number][];
  /** The largest p90 over all pairs (-1 unknown). */
  worst: number;
}

/**
 * v1.2: the net port, a MessageChannel the host hands to the game's worker at load (through the loader, which never
 * reads it), so packets skip the loader's main thread.
 */
export type HostToRunnerNet =
  | ({ t: 'session' } & NetStart) // model "link": the session starts while the game runs
  | { t: 'recv'; peer: number; ch: number; bytes: ArrayBuffer }
  | { t: 'peer'; peer: number; state: number }
  | { t: 'rtt'; rtt: number[] }; // live round trip per index, ms (-1 unknown)

export type RunnerToHostNet = { t: 'send'; peer: number; ch: number; bytes: ArrayBuffer };

/** Validate a packet the game wants to send (peer -1 = every other peer). */
export function checkNetSend(d: unknown): RunnerToHostNet | null {
  if (!d || typeof d !== 'object') return null;
  const m = d as Record<string, unknown>;
  if (m.t !== 'send' || !int(m.peer) || m.peer < -1 || m.peer > 3 || !int(m.ch) || !(m.bytes instanceof ArrayBuffer)) return null;
  const max = channelMax(m.ch);
  return max >= 0 && m.bytes.byteLength <= max ? { t: 'send', peer: m.peer, ch: m.ch, bytes: m.bytes } : null;
}

export type HostToSandbox =
  | ({ t: 'load' } & LoadPayload & { netPort?: MessagePort })
  | { t: 'input'; pads: PadState[] }
  | { t: 'mouse'; dx: number; dy: number; buttons: number }
  | { t: 'wheel'; dy: number } // v1.1: 1/100 notch, positive = towards the player
  | { t: 'give-file'; id: number; bytes: ArrayBuffer } // v1.1: a file the player picked for fileInputs[id]
  | { t: 'pause'; on: boolean }
  | { t: 'option'; id: number; value: number }
  | { t: 'net-mode'; on: boolean } // v1.2: in a session the loader never pauses and keeps stepping while hidden
  | { t: 'probe-network'; urls: string[] }; // test hook: the loader reports what a fetch from the sandbox does

export type SandboxToHost =
  | { t: 'ready' }
  | { t: 'started'; width: number; height: number }
  | { t: 'stats'; frames: number; fps: number; width: number; height: number }
  | { t: 'log'; text: string }
  | { t: 'rumble'; port: number; on: boolean }
  | { t: 'pointer-lock'; on: boolean }
  | { t: 'request-file'; id: number }
  | { t: 'option-changed'; id: number; value: number } // v1.1: the game changed one of its options itself
  | { t: 'file'; name: string; mime: string; bytes: ArrayBuffer }
  | { t: 'save'; generation: number; bytes: ArrayBuffer }
  | { t: 'audio'; rate: number; samples: ArrayBuffer }
  | { t: 'key'; code: string; down: boolean }
  | { t: 'gesture' } // the player tapped/clicked the game: the host may start audio now
  | { t: 'error'; message: string; detail: string }
  | { t: 'probe-result'; results: string[] };

// Loader (sandbox main thread) <-> runner (dedicated worker running game code).
export type LoaderToRunner =
  // video "webgl": the loader's canvas, transferred; view: the size for the first rtw_resize (CSS px, dpr x100)
  | ({ t: 'load' } & LoadPayload & { canvas?: OffscreenCanvas; view?: { w: number; h: number; dpr: number }; netPort?: MessagePort })
  | { t: 'frames'; n: number; pads: PadState[] }
  | { t: 'mouse'; dx: number; dy: number; buttons: number }
  | { t: 'wheel'; dy: number }
  | { t: 'give-file'; id: number; bytes: ArrayBuffer }
  | { t: 'pause'; on: boolean }
  | { t: 'option'; id: number; value: number }
  | { t: 'resize'; w: number; h: number; dpr: number };

export type RunnerToLoader =
  | { t: 'started'; width: number; height: number }
  | { t: 'frame'; fb: ArrayBuffer | null; width: number; height: number; ran: number }
  | { t: 'log'; text: string }
  | { t: 'rumble'; port: number; on: boolean }
  | { t: 'pointer-lock'; on: boolean }
  | { t: 'request-file'; id: number }
  | { t: 'option-changed'; id: number; value: number }
  | { t: 'file'; name: string; mime: string; bytes: ArrayBuffer }
  | { t: 'save'; generation: number; bytes: ArrayBuffer }
  | { t: 'audio'; rate: number; samples: ArrayBuffer }
  | { t: 'error'; message: string; detail: string };

export /** A wheel event in 1/100 notch (positive = towards the player): ~100 px or 3 lines per notch. */
function wheelUnits(e: { deltaY: number; deltaMode: number }): number {
  const k = e.deltaMode === 1 ? 100 / 3 : e.deltaMode === 2 ? 300 : 1;
  return Math.max(-32768, Math.min(32767, Math.round(e.deltaY * k)));
}

// (kept here, not in manifest.ts, so the sandbox loader's bundle never pulls in the schema validator)
type OptionDef = NonNullable<Manifest['options']>[number];

/** Whether `value` is a valid value of option `o`: 0/1 for bool, a listed choice for enum, an i32 within min/max for int. */
export function optionValueOk(o: OptionDef, value: number): boolean {
  if (!Number.isInteger(value) || value < -0x80000000 || value > 0x7fffffff) return false;
  if (o.type === 'bool') return value === 0 || value === 1;
  if (o.type === 'enum') return (o.choices ?? []).some((c) => c.value === value);
  return (o.min === undefined || value >= o.min) && (o.max === undefined || value <= o.max);
}

const str = (v: unknown, max: number): v is string => typeof v === 'string' && v.length <= max;
const int = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);

/**
 * Validate a message that arrived from the sandbox. Returns null for anything malformed. `manifest` (the game's
 * options) is needed for messages that name an option: without it they are refused.
 */
export function checkSandboxMsg(d: unknown, manifest?: Pick<Manifest, 'options'>): SandboxToHost | null {
  if (!d || typeof d !== 'object') return null;
  const m = d as Record<string, unknown>;
  switch (m.t) {
    case 'ready': return { t: 'ready' };
    case 'gesture': return { t: 'gesture' };
    case 'started': return int(m.width) && int(m.height) ? { t: 'started', width: m.width, height: m.height } : null;
    case 'stats': return int(m.frames) && typeof m.fps === 'number' && int(m.width) && int(m.height)
      ? { t: 'stats', frames: m.frames, fps: m.fps, width: m.width, height: m.height } : null;
    case 'log': return str(m.text, 4096) ? { t: 'log', text: m.text } : null;
    case 'rumble': return int(m.port) && typeof m.on === 'boolean' ? { t: 'rumble', port: m.port, on: m.on } : null;
    case 'pointer-lock': return typeof m.on === 'boolean' ? { t: 'pointer-lock', on: m.on } : null;
    case 'request-file': return int(m.id) && m.id >= 1 && m.id <= 255 ? { t: 'request-file', id: m.id } : null;
    case 'option-changed': {
      // only a declared option, with a value that option allows
      const o = int(m.id) ? manifest?.options?.find((x) => x.id === m.id) : undefined;
      return o && int(m.value) && optionValueOk(o, m.value) ? { t: 'option-changed', id: o.id, value: m.value } : null;
    }
    case 'file': return str(m.name, 256) && str(m.mime, 128) && m.bytes instanceof ArrayBuffer && m.bytes.byteLength <= 64 << 20
      ? { t: 'file', name: m.name, mime: m.mime, bytes: m.bytes } : null;
    case 'save': return int(m.generation) && m.bytes instanceof ArrayBuffer && m.bytes.byteLength <= 1 << 20
      ? { t: 'save', generation: m.generation, bytes: m.bytes } : null;
    case 'audio': return int(m.rate) && m.samples instanceof ArrayBuffer ? { t: 'audio', rate: m.rate, samples: m.samples } : null;
    case 'key': return str(m.code, 64) && typeof m.down === 'boolean' ? { t: 'key', code: m.code, down: m.down } : null;
    case 'error': return str(m.message, 4096) && str(m.detail, 65536) ? { t: 'error', message: m.message, detail: m.detail } : null;
    case 'probe-result': return Array.isArray(m.results) && m.results.every((r) => str(r, 1024))
      ? { t: 'probe-result', results: m.results as string[] } : null;
    default: return null;
  }
}
